import React, {
  useState,
  createRef,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  useHistory,
  useLocation,
} from 'react-router-dom';
import Select from 'react-select';
import {
  Form,
  FormGroup,
  Label,
} from 'reactstrap';
import _ from 'lodash';
import { useIntl } from 'react-intl';

import { setSearchTerm } from '../actions/Search';
import { setFilesParams } from '../actions/Files';

import { APIsearch } from '../api/Search';

import SearchSelectItem from './Search/SelectItem';
import SearchSelectGroup from './Search/SelectGroup';
import SearchSelectGroupHeading from './Search/SelectGroupHeading';
import SearchQueryType from './Search/SearchQueryType';
import SearchNoOptions from './Search/NoOptions';

import { thumbnailPlaceholder } from '../helpers/Collections';
import { getSetting, getUserSetting } from '../helpers/Settings';
import { getCurrentURLParams, handleURLParams } from '../helpers/URLParams';

const SearchV2 = ({
  profile,
  searchQueryType,
  search,
  setSearch,
  pageSearchPath,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [searched, setSearched] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enterClicked, setEnterClicked] = useState(false);
  const [showSearchesDropdown, setShowSearchesDropdown] = useState(true);

  const enterClickedRef = useRef(enterClicked);

  const intl = useIntl();

  const history = useHistory();
  const location = useLocation();

  const selectRef = createRef();

  useEffect(() => {
    selectRef.current.select.getNextFocusedOption = () => null;
  }, [selectRef]);

  useEffect(() => {
    enterClickedRef.current = enterClicked;
  }, [enterClicked]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadOptionsSelect = useCallback(
    _.debounce((s) => {
      const searchOptions = [];
      const files = [];
      const collections = [];
      const filesArchived = [];
      const collectionsArchived = [];

      if (enterClickedRef.current || pageSearchPath) {
        return;
      }

      if (s.length === 0) {
        setOptions([]);
        setSearched(false);
        return;
      }

      setLoading(true);
      APIsearch(s, { query_type: searchQueryType }).then((response) => {
        const { data } = response;

        if (data && data.files) {
          data.files.forEach((item) => {
            files.push({
              value: item.uuid,
              uuid: item.uuid,
              label: item.display_name,
              urls: {
                thumbnail: item.urls.thumbnail,
                thumbnailx2: item.urls.thumbnailx2,
              },
              type: 'file',
              readable_size: item.readable_size,
              path: item.path,
              attributes: item.attributes,
              type_group: item.type_group,
              website: item.website || null,
            });
          });
        }

        if (data && data.collections) {
          data.collections.forEach((item) => {
            collections.push({
              value: item.uuid,
              uuid: item.uuid,
              label: item.name,
              urls: {
                thumbnail: item.featured_file ?
                  item.featured_file.urls.thumbnail :
                  thumbnailPlaceholder,
                thumbnailx2: item.featured_file ?
                  item.featured_file.urls.thumbnailx2 :
                  thumbnailPlaceholder,
              },
              ancestors: item.ancestors,
              type: 'collection',
            });
          });
        }

        if (data && data.files_archived) {
          filesArchived.push({
            value: data.files_archived,
            type: 'files_archived',
          });
        }
        if (data && data.collections_archived) {
          collectionsArchived.push({
            value: data.collections_archived,
            type: 'collections_archived',
          });
        }

        if (getSetting('search_order') === 'collections') {
          searchOptions.push({
            label: intl.formatMessage({ defaultMessage: 'Collections' }),
            options: collections,
            search: s,
            results: null,
          });
          searchOptions.push({
            label: intl.formatMessage({ defaultMessage: 'Files' }),
            options: files,
            search: s,
            results: null,
          });
        } else {
          searchOptions.push({
            label: intl.formatMessage({ defaultMessage: 'Files' }),
            options: files,
            search: s,
            results: null,
          });
          searchOptions.push({
            label: intl.formatMessage({ defaultMessage: 'Collections' }),
            options: collections,
            search: s,
            results: null,
          });
        }
        searchOptions.push({
          label: intl.formatMessage({ defaultMessage: 'Archived files' }),
          options: filesArchived,
          search: s,
          results: data.files_archived,
          type: 'file',
        });
        searchOptions.push({
          label: intl.formatMessage({ defaultMessage: 'Archived Collections' }),
          options: collectionsArchived,
          search: s,
          results: data.collections_archived,
          type: 'collection',
        });

        setOptions(searchOptions);
        setLoading(false);
        setSearched(true);
      });
    }, 800),
    [enterClicked, search, searchQueryType, pageSearchPath],
  );

  const clearSearchInput = () => {
    setSearchValue('');
    setOptions([]);
  };

  useEffect(() => {
    if (!search) {
      clearSearchInput();
    } else if (pageSearchPath || enterClickedRef.current) {
      setSearchValue(search);
    }
  }, [search, pageSearchPath]);

  const historyPush = (pathname, searchQuery = '') => {
    history.push({
      pathname,
      search: searchQuery,
      state: {
        search: searchValue,
      },
    });
  };

  const handleSelect = (value, action) => {
    let currentSearchParams = getCurrentURLParams(location);

    const params = new URLSearchParams(currentSearchParams);

    if (pageSearchPath) {
      if (action === 'clear') {
        clearSearchInput();
        return;
      }
      if (action === 'set-value') {
        params.delete('page');
        if (searchValue.trim()) {
          params.set('search', searchValue.trim());
        } else {
          params.delete('search');
        }
        history.push({ pathname: pageSearchPath, search: params.toString() });
      }
      return;
    }

    if (searchQueryType) {
      params.set('query_type', searchQueryType);
    }
    currentSearchParams = params.toString();

    if (action === 'set-value') {
      setSearch(searchValue);
      clearSearchInput();
      historyPush('/results', currentSearchParams);

      if (value.indexOf('group-') === 0) {
        const valueParts = value.split('-');
        if (valueParts[1]) {
          setSearched(false);
          if (valueParts[1] === 'files') {
            historyPush('/files', currentSearchParams);
          }
          if (valueParts[1] === 'collections') {
            historyPush('/collections', currentSearchParams);
          }
          if (valueParts[1] === 'archivedfiles') {
            historyPush('/archive/files', currentSearchParams);
          }
          if (valueParts[1] === 'archivedcollections') {
            historyPush('/archive/collections', currentSearchParams);
          }
        }
      }
    }

    if (action === 'select-option') {
      if (value.type === 'file') {
        historyPush(`/files/${value.uuid}`);
      }

      if (value.type === 'collection') {
        historyPush(`/collections/${value.uuid}`);
        clearSearchInput();
        setSearch('');
      }
    }

    if (action === 'clear') {
      clearSearchInput();
    }
  };

  const handleEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const isOptionSelected = !selectRef.current.select.state.focusedOption;

      if (isOptionSelected) {
        setEnterClicked(true);
        setSearched(false);
        setShowSearchesDropdown(false);
        handleSelect(searchValue, 'set-value');
        handleURLParams({ search: searchValue }, history);
      }
    }
  };

  const handleChange = (inputValue, action) => {
    if (action.action === 'input-change') {
      setSearchValue(inputValue);
      setEnterClicked(false);
      if (!pageSearchPath) {
        loadOptionsSelect(inputValue);
      }
    }
  };

  const pinnedSearches = getUserSetting('pinned_searches') || [];
  const latestSearches = getUserSetting('latest_searches') || [];

  const shouldDisplayMenu = () => {
    if (pageSearchPath) return false;
    if (profile && !profile.uuid) return true;
    if (search && searched) return true;
    if (
      showSearchesDropdown &&
      !search &&
      (pinnedSearches.length > 0 || latestSearches.length > 0)
    ) return true;
    return false;
  };

  return (
    <Form className="nav-search" onKeyDown={handleEnter}>
      <SearchQueryType allowDisabled={true} prefix="main-" external={!!pageSearchPath} />
      <FormGroup>
        <Label for="inp-search-input" className="label-search" aria-label={intl.formatMessage({ defaultMessage: 'Search file or Collection name, keyword or tag' })}>
          <i className="fas fa-search" />
        </Label>
        <Select
          ref={selectRef}
          name="search"
          id="inp-search"
          inputId="inp-search-input"
          className="search-input"
          classNamePrefix="search-input"
          placeholder={intl.formatMessage({ defaultMessage: 'Search file or Collection name, keyword or tag' })}
          components={{
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            Option: SearchSelectItem,
            Group: SearchSelectGroup,
            GroupHeading: SearchSelectGroupHeading,
            NoOptionsMessage: (props) => (
              <SearchNoOptions
                {...props}
                searched={searched}
                search={searchValue}
                profile={profile}
                show={showSearchesDropdown}
              />
            ),
          }}
          isClearable={true}
          escapeClearsValue={true}
          options={options}
          onInputChange={handleChange}
          onChange={(value, { action }) => {
            handleSelect(value, action);
          }}
          inputValue={searchValue}
          value={searchValue}
          isLoading={loading}
          filterOption={() => (true)}
          styles={{
            menu: (provided) => ({
              ...provided,
              display: shouldDisplayMenu() ? provided.display : 'none',
            }),
          }}
          onFocus={() => setShowSearchesDropdown(true)}
        />
        {(searchValue && !loading) && (
          <a
            href="#clear"
            className="search-clear"
            onClick={(e) => {
              e.preventDefault();
              handleSelect(null, 'clear');
            }}
          >
            <i className="fas fa-times" />
          </a>
        )}
      </FormGroup>
    </Form>
  );
};

const mapDispatchToProps = dispatch => ({
  setSearch: (searchValue) => {
    dispatch(setFilesParams({ orderby: 'relevance', noFilters: false }));
    dispatch(setSearchTerm(searchValue));
  },
});

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  searchQueryType: state.Search.query_type,
  search: state.Search.search,
});

SearchV2.propTypes = {
  setSearch: PropTypes.func.isRequired,
  profile: PropTypes.object,
  searchQueryType: PropTypes.string,
  search: PropTypes.string,
  pageSearchPath: PropTypes.number,
};

SearchV2.defaultProps = {
  profile: null,
  searchQueryType: null,
  search: null,
  pageSearchPath: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchV2);
